import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { oziusTableData, summerTableData, winterTableData } from "./data";
import { StepComponentProps } from "../step-config-file/types";

interface ParcelCardProps {
  properties_layers: any;
  setIsOpen: (value: boolean) => void;
}

const DATASETS = [
  { value: "satsure-summer", label: "Satsure - Summer" },
  { value: "satsure-winter", label: "Satsure - Winter" },
  { value: "ozius", label: "Ozius" },
] as const;

type DatasetValue = (typeof DATASETS)[number]["value"];

const ParcelCard = ({ properties_layers, setIsOpen }: ParcelCardProps) => {
  const dataSatsure = properties_layers.find(
    (item: { layer: { id: string } }) =>
      item.layer.id === "land-use-layer-satsure",
  );

  const dataOzius = properties_layers.find(
    (item: { layer: { id: string } }) =>
      item.layer.id === "land-use-layer-ozius",
  );

  const tableDataFor = (value: DatasetValue) => {
    if (value === "satsure-winter")
      return winterTableData(dataSatsure.properties);
    if (value === "satsure-summer")
      return summerTableData(dataSatsure.properties);
    return oziusTableData(dataOzius.properties);
  };

  return (
    <>
      <div className="flex cursor-move items-start justify-between p-0">
        <div>
          <p className="text-sm font-medium">
            {properties_layers[0].properties.parcel_id}
          </p>
          <p className="text-xs text-muted-foreground">
            {`${properties_layers[0].properties.cadastre_pid} - ${properties_layers[0].properties.cadastre_polygon_pid}`}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="close"
          className="size-7"
          onClick={() => setIsOpen(false)}
        >
          <X className="size-4" />
        </Button>
      </div>

      <Tabs defaultValue="satsure-summer" className="pt-4">
        <TabsList>
          {DATASETS.map((dataset) => (
            <TabsTrigger key={dataset.value} value={dataset.value}>
              {dataset.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {DATASETS.map((dataset) => (
          <TabsContent key={dataset.value} value={dataset.value}>
            <LandDataTable
              tableData={tableDataFor(dataset.value)}
              activeDataset={dataset.label}
            />
          </TabsContent>
        ))}
      </Tabs>
    </>
  );
};

interface LandEntry {
  landUse: string;
  hectare: number;
  percentage: number | null;
}

interface TableData {
  landUse?: LandEntry[];
  landCover?: LandEntry[];
  cropping?: LandEntry[];
  harvest?: LandEntry[];
}

interface TableSectionProps {
  title: string;
  data?: LandEntry[];
  showPercentage?: boolean;
}

const TableSection = ({
  title,
  data,
  showPercentage = true,
}: TableSectionProps) => {
  const formatNumber = (value: number | undefined): string => {
    if (value === undefined) return "0.00";
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  if (!data?.length) return null;

  return (
    <>
      <TableHeader>
        <TableRow>
          <TableHead className="w-1/2 text-left font-bold">{title}</TableHead>
          <TableHead className="w-3/10 text-left font-bold">Hectare</TableHead>
          {showPercentage && (
            <TableHead className="w-1/5 text-right font-bold">
              Percentage
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((entry, index) => (
          <TableRow key={`${title}-${index}`} className="border-0">
            <TableCell className="w-1/2 text-left">{entry.landUse}</TableCell>
            <TableCell className="w-3/10 text-left">
              {formatNumber(entry.hectare)}
            </TableCell>
            {showPercentage && (
              <TableCell className="w-1/5 text-right">
                {entry.landUse == "Total Land Cover Area" ||
                entry.landUse == "Total Land Use"
                  ? ""
                  : entry.percentage != null
                    ? `${entry.percentage.toFixed(2)}%`
                    : "0.00%"}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </>
  );
};

interface LandDataTableProps {
  tableData: TableData;
  activeDataset: string;
}

export const LandDataTable = ({
  tableData,
  activeDataset,
}: LandDataTableProps) => {
  return (
    <div className="overflow-hidden">
      <Table>
        <TableSection
          title="Land Use"
          data={tableData.landUse}
          showPercentage={activeDataset == "Ozius"}
        />
        <TableSection title="Land Cover" data={tableData.landCover} />
        {tableData.harvest && (
          <>
            <TableSection title="Cropping" data={tableData.cropping} />
            <TableSection
              title="Harvest"
              data={tableData.harvest}
              showPercentage={false}
            />
          </>
        )}
      </Table>
    </div>
  );
};

export const NabLandUseComponent = (props: StepComponentProps) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const nodeRef = useRef(null);

  const attributes = props.attributes ?? [];
  const firstAttribute = attributes[0] ?? {
    geometry: {},
    properties: {},
    layer: {},
    source: "",
  };

  const { layer_name } = props;

  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (firstAttribute.layer?.id === layer_name) {
      setIsOpen(true);
    } else setIsOpen(false);
  }, [firstAttribute.layer?.id, layer_name, firstAttribute.properties]);

  if (firstAttribute.layer?.id === layer_name && isOpen) {
    return (
      <Draggable
        nodeRef={nodeRef}
        position={position}
        onStop={(_, data) => setPosition({ x: data.x, y: data.y })}
      >
        <div ref={nodeRef} className="fixed bottom-4 right-4">
          <Card className="flex w-md">
            <CardContent>
              <ParcelCard
                properties_layers={attributes}
                setIsOpen={setIsOpen}
              />
            </CardContent>
          </Card>
        </div>
      </Draggable>
    );
  }

  return <></>;
};
